
import { useState, useEffect } from 'react';

export default function AboutHero() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [counters, setCounters] = useState({
    projects: 0,
    countries: 0,
    years: 0,
    solutions: 0,
  });

  useEffect(() => {
    setIsLoaded(true);
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const ease = 1 - Math.pow(1 - progress, 3);
      setCounters({
        projects: Math.round(150 * ease),
        countries: Math.round(5 * ease),
        years: Math.round(4 * ease),
        solutions: Math.round(360 * ease),
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, []);

  const stats = [
    {
      value: `${counters.projects}+`,
      label: 'Projects Delivered',
      icon: 'ri-folder-chart-line',
      color: '#038C7F',
    },
    {
      value: `${counters.countries}`,
      label: 'Countries Served',
      icon: 'ri-earth-line',
      color: '#F2780C',
    },
    {
      value: `${counters.years}+`,
      label: 'Years Experience',
      icon: 'ri-calendar-check-line',
      color: '#038C7F',
    },
    {
      value: `${counters.solutions}°`,
      label: 'Creative Solutions',
      icon: 'ri-refresh-line',
      color: '#F2780C',
    },
  ];

  // Helper to safely navigate (fallback for environments where the global
  // navigation function might be missing)
  const safeNavigate = (path) => {
    try {
      if (typeof window !== 'undefined' && typeof window.REACT_APP_NAVIGATE === 'function') {
        window.REACT_APP_NAVIGATE(path);
      } else {
        // Fallback: use native navigation
        window.location.href = path;
      }
    } catch (err) {
      console.error('Navigation error:', err);
    }
  };

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-[#0D0D0D]">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dramatic%20aerial%20cinematic%20view%20of%20modern%20Kampala%20Uganda%20creative%20agency%20office%20interior%20with%20warm%20golden%20hour%20lighting%20contemporary%20African%20inspired%20design%20elements%20professional%20workspace%20collaborative%20atmosphere%20vibrant%20creative%20energy%20premium%20photography%20wide%20angle&width=1920&height=1080&seq=about-hero-v3-001&orientation=landscape"
          alt="Asili Visuals Creative Space"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D] via-[#0D0D0D]/80 to-[#0D0D0D]/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-[#0D0D0D]/70" />
      </div>

      {/* Grid Overlay */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(#038C7F 1px, transparent 1px), linear-gradient(90deg, #038C7F 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              backgroundColor: i % 2 === 0 ? '#038C7F' : '#F2780C',
              opacity: 0.3,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `aboutFloat ${6 + Math.random() * 8}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex items-center pt-24 lg:pt-0">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Left – Text */}
            <div className="lg:col-span-7">
              <div
                className={`transition-all duration-1000 ${
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
              >
                {/* Badge */}
                <div
                  className={`inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6 transition-all duration-700 delay-100 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                  }`}
                >
                  <div className="w-2 h-2 bg-[#038C7F] rounded-full animate-pulse" />
                  <span className="text-[10px] sm:text-xs font-bold text-white/70 uppercase tracking-[0.2em]">
                    About Äsili Visuals
                  </span>
                </div>

                {/* Headline */}
                <h1 className="mb-6">
                  <div className="overflow-hidden">
                    <div
                      className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.9] tracking-tighter transition-all duration-700 delay-200 ${
                        isLoaded ? 'translate-y-0' : 'translate-y-full'
                      }`}
                    >
                      Building Brands
                    </div>
                  </div>
                  <div className="overflow-hidden mt-2">
                    <div
                      className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[0.9] tracking-tighter transition-all duration-700 delay-300 ${
                        isLoaded ? 'translate-y-0' : 'translate-y-full'
                      }`}
                    >
                      That{' '}
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] via-[#05a99a] to-[#F2780C]">
                        Define Markets
                      </span>
                    </div>
                  </div>
                </h1>

                {/* Tagline */}
                <div
                  className={`flex items-start gap-4 mb-6 transition-all duration-700 delay-400 ${
                    isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                  }`}
                >
                  <div className="w-14 h-[2px] bg-gradient-to-r from-[#038C7F] to-[#F2780C] mt-3 flex-shrink-0" />
                  <div>
                    <span className="text-lg sm:text-xl font-serif italic text-white/90 leading-snug block">
                      An integrated advertising firm rooted in Kampala,
                    </span>
                    <span className="text-lg sm:text-xl font-serif italic text-[#F2780C]/90 leading-snug block">
                      serving brands across East Africa.
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p
                  className={`text-sm sm:text-base text-gray-400 leading-relaxed max-w-lg mb-8 transition-all duration-700 delay-500 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                  }`}
                >
                  We develop structured communication systems that strengthen credibility,
                  expand markets, and enhance long-term brand equity for businesses,
                  institutions, and governments.
                </p>

                {/* CTA Buttons */}
                <div
                  className={`flex flex-wrap gap-3 sm:gap-4 transition-all duration-700 delay-600 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                  }`}
                >
                  <a
                    href="/"
                    onClick={(e) => {
                      e.preventDefault();
                      safeNavigate('/');
                    }}
                    className="group relative px-7 py-3.5 bg-[#038C7F] text-white text-sm font-bold rounded-full overflow-hidden whitespace-nowrap cursor-pointer"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      View Our Work
                      <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform" />
                    </span>
                    <div className="absolute inset-0 bg-[#F2780C] translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                  </a>

                  <a
                    href="#team"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('#team')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="group px-7 py-3.5 border-2 border-white/20 text-white text-sm font-bold rounded-full hover:border-[#F2780C] hover:bg-[#F2780C]/10 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-team-line text-lg" />
                    Meet The Team
                  </a>
                </div>
              </div>
            </div>

            {/* Right – Stats Grid */}
            <div className="lg:col-span-5 hidden lg:block">
              <div
                className={`grid grid-cols-2 gap-3 transition-all duration-700 delay-600 ${
                  isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
              >
                {stats.map((stat, idx) => (
                  <div
                    key={idx}
                    className="group bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 cursor-default"
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                      style={{ backgroundColor: `${stat.color}20` }}
                    >
                      <i className={`${stat.icon} text-xl`} style={{ color: stat.color }} />
                    </div>
                    <div className="text-3xl font-black text-white mb-0.5">{stat.value}</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Service chips */}
              <div
                className={`flex flex-wrap justify-end gap-2 mt-4 transition-all duration-700 delay-800 ${
                  isLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {['Strategy', 'Branding', 'Film', 'Digital', 'Outdoor'].map((tag, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-[10px] font-semibold text-white/50 uppercase tracking-wider hover:text-white/80 hover:border-white/20 transition-all cursor-default"
                  >
                    {tag}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile Stats */}
          <div
            className={`lg:hidden flex items-center gap-5 mt-8 pt-6 border-t border-white/10 transition-all duration-700 delay-700 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {[
              { val: '150+', label: 'Projects', color: '#038C7F' },
              { val: '5', label: 'Countries', color: '#F2780C' },
              { val: '360°', label: 'Solutions', color: '#038C7F' },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-5">
                {i > 0 && <div className="w-px h-10 bg-white/10" />}
                <div>
                  <div className="text-2xl font-black text-white">
                    {s.val.replace(/[+°]/g, '')}
                    <span style={{ color: s.color }}>
                      {s.val.includes('+')
                        ? '+'
                        : s.val.includes('°')
                        ? '°'
                        : ''}
                    </span>
                  </div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Gradient Line */}
      <div className="relative z-10 h-1 w-full bg-gradient-to-r from-[#038C7F] via-[#F2780C] to-[#038C7F]" />

      {/* Keyframe animation – extracted into a <style jsx> block to avoid JSX parsing errors */}
      <style jsx>{`
        @keyframes aboutFloat {
          0%,
          100% {
            transform: translateY(0px);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-20px);
            opacity: 0.6;
          }
        }
      `}</style>
    </section>
  );
}
